// File service for handling marketing materials uploads and storage
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { collection, addDoc, getDocs, deleteDoc, doc, query, where, updateDoc } from 'firebase/firestore';
import { storage, db } from '../firebase';
import { getEffectiveAccountId } from './authorizationService';

class FileService {
  constructor() {
    this.storage = storage;
    this.maxFileSize = 10 * 1024 * 1024; // 10MB
    this.allowedTypes = [
      'image/jpeg',
      'image/png',
      'image/gif',
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation'
    ];
  }

  // Validate file before upload
  validateFile(file) {
    const errors = [];

    if (file.size > this.maxFileSize) {
      errors.push(`File size must be less than ${this.maxFileSize / (1024 * 1024)}MB`);
    }

    if (!this.allowedTypes.includes(file.type)) {
      errors.push('File type not supported. Please upload images, PDFs, or Office documents.');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  // Upload file to Firebase Storage and store metadata in Firestore
  async uploadFile(file, accountId, category = 'general') {
    try {
      const validation = this.validateFile(file);
      if (!validation.isValid) {
        throw new Error(validation.errors.join(', '));
      }

      // Use effective account ID for scoping
      const effectiveAccountId = getEffectiveAccountId() || accountId;
      if (!effectiveAccountId) {
        throw new Error('No account scope set. Please select an account.');
      }

      const timestamp = Date.now();
      const fileName = `${timestamp}_${file.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`;
      const filePath = `accounts/${effectiveAccountId}/materials/${category}/${fileName}`;
      const storageRef = ref(this.storage, filePath);

      // Upload to Firebase Storage
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);

      // Store metadata in Firestore
      const fileMetadata = {
        name: file.name,
        originalName: file.name,
        size: file.size,
        type: file.type,
        url: downloadURL,
        path: filePath,
        category,
        uploadedAt: timestamp,
        uploadedBy: effectiveAccountId,
        accountId: effectiveAccountId
      };

      const docRef = await addDoc(collection(db, 'marketing_materials'), fileMetadata);

      return {
        id: docRef.id,
        ...fileMetadata
      };
    } catch (error) {
      console.error('File upload error:', error);
      throw error;
    }
  }

  // Get download URL for a file
  async getFileURL(filePath) {
    try {
      const storageRef = ref(this.storage, filePath);
      return await getDownloadURL(storageRef);
    } catch (error) {
      console.error('Error getting file URL:', error);
      throw error;
    }
  }

  // Delete file from storage and Firestore
  async deleteFile(fileId, filePath = null) {
    try {
      // Delete from Firestore first
      await deleteDoc(doc(db, 'marketing_materials', fileId));
      
      // Delete from Storage if path is provided
      if (filePath) {
        const storageRef = ref(this.storage, filePath);
        await deleteObject(storageRef);
      }
      
      return true;
    } catch (error) {
      console.error('Error deleting file:', error);
      throw error;
    }
  }

  // Update file category in Firestore
  async updateFileCategory(fileId, newCategory) {
    try {
      const fileRef = doc(db, 'marketing_materials', fileId);
      await updateDoc(fileRef, {
        category: newCategory.trim().toLowerCase()
      });
      
      return true;
    } catch (error) {
      console.error('Error updating file category:', error);
      throw error;
    }
  }

  // List all files for an account
  async listUserFiles(accountId, category = null) {
    try {
      // Use effective account ID for scoping
      const effectiveAccountId = getEffectiveAccountId() || accountId;
      if (!effectiveAccountId) {
        console.log('No effective account ID, cannot list files');
        return [];
      }

      // Query Firestore for file metadata
      let q = query(
        collection(db, 'marketing_materials'),
        where('accountId', '==', effectiveAccountId)
      );

      // Add category filter if specified
      if (category && category !== 'all') {
        q = query(
          collection(db, 'marketing_materials'),
          where('accountId', '==', effectiveAccountId),
          where('category', '==', category)
        );
      }

      const querySnapshot = await getDocs(q);
      const files = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        files.push({
          id: doc.id,
          ...data
        });
      });

      // Sort by uploadedAt in JavaScript to avoid Firestore index requirement
      files.sort((a, b) => {
        const aTime = a.uploadedAt || 0;
        const bTime = b.uploadedAt || 0;
        return bTime - aTime; // Descending order (newest first)
      });

      return files;
    } catch (error) {
      console.error('Error listing files:', error);
      // Return empty array instead of throwing to prevent UI crashes
      return [];
    }
  }

  // Generate thumbnail for image files
  async generateThumbnail(file, maxWidth = 200, maxHeight = 200) {
    return new Promise((resolve) => {
      if (!file.type.startsWith('image/')) {
        resolve(null);
        return;
      }

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        const { width, height } = img;
        let newWidth = width;
        let newHeight = height;

        if (width > maxWidth) {
          newWidth = maxWidth;
          newHeight = (height * maxWidth) / width;
        }

        if (newHeight > maxHeight) {
          newHeight = maxHeight;
          newWidth = (newWidth * maxHeight) / newHeight;
        }

        canvas.width = newWidth;
        canvas.height = newHeight;

        ctx.drawImage(img, 0, 0, newWidth, newHeight);
        
        canvas.toBlob((blob) => {
          resolve(blob);
        }, 'image/jpeg', 0.8);
      };

      img.src = URL.createObjectURL(file);
    });
  }
}

const fileService = new FileService();
export default fileService;
